import type { QueuedOverlay } from '../store/overlay/overlaySlice';
import type { EvidenceBucket, ItemWithScene, Location } from '../types';
import shuffleArray from './shuffle';

/**
 * Counts evidence items that are not marked as "last item"
 * Last items only appear after all other items are found
 */
export const getNonLastItemCount = (evidence: ItemWithScene[]): number =>
  evidence.filter((item) => !item.isLastItem).length;

/**
 * Checks if all non-last items have been found
 * Used to determine when the last item should become available
 */
export const areAllNonLastItemsFound = (
  evidence: ItemWithScene[],
  foundEvidence: ItemWithScene[],
): boolean =>
  getNonLastItemCount(foundEvidence) >= getNonLastItemCount(evidence);

/**
 * Creates an evidence bucket for a specific location
 */
export const createLocationEvidenceBucket = (
  location: Location,
  locationIndex: number,
): EvidenceBucket => {
  // Collect all evidence from all scenes in this location
  const allEvidence: ItemWithScene[] = [];

  location.scenes.forEach((scene, sceneIndex) => {
    // Add all items from the scene
    scene.items.forEach((item) => {
      allEvidence.push({
        ...item,
        sceneIndex,
        sceneId: scene.id,
        locationIndex,
        locationId: location.id,
      });
    });
  });

  // Randomize the evidence array
  const randomizedEvidence = shuffleArray(allEvidence);

  // Initialize unlocked scenes based on mode
  const isSequential = location.gameConfig?.sequentialSceneUnlocking === true;
  let initialUnlockedScenes: string[];

  if (isSequential) {
    // Sequential mode: only unlock the first scene (plus any consecutive empty scenes)
    initialUnlockedScenes = [];
    for (const scene of location.scenes) {
      initialUnlockedScenes.push(scene.id);
      if (scene.items.length > 0) break; // Stop at first scene with items
    }
  } else {
    // Default mode: unlock all scenes without an unlockRequiredItemId
    initialUnlockedScenes = location.scenes
      .filter((scene) => !scene.unlockRequiredItemId)
      .map((scene) => scene.id);

    // Also include the first scene if it's not already included (first scene is always unlocked)
    if (
      location.scenes.length > 0 &&
      !initialUnlockedScenes.includes(location.scenes[0].id)
    ) {
      initialUnlockedScenes.push(location.scenes[0].id);
    }
  }

  // Find up to 3 evidence items from unlocked scenes to set as initial active clues
  // Note: isLastItem items are excluded - they only appear after all other items are found
  const maxActiveClues = 3;
  const initialActiveClueIndices: number[] = [];
  for (
    let i = 0;
    i < randomizedEvidence.length &&
    initialActiveClueIndices.length < maxActiveClues;
    i++
  ) {
    const evidenceItem = randomizedEvidence[i];
    // Skip last items - they should not appear in initial active clues
    if (evidenceItem.isLastItem) continue;

    if (initialUnlockedScenes.includes(evidenceItem.sceneId)) {
      initialActiveClueIndices.push(i);
    }
  }

  // FTUE: Ensure at least one active clue is from the first scene (where player starts)
  // This guarantees the hint highlight will work on the starting scene
  // Note: isLastItem items are excluded from FTUE consideration
  if (location.scenes.length > 0 && initialActiveClueIndices.length > 0) {
    const firstSceneId = location.scenes[0].id;
    const hasFirstSceneClue = initialActiveClueIndices.some(
      (index) => randomizedEvidence[index].sceneId === firstSceneId,
    );

    if (!hasFirstSceneClue) {
      // Find first evidence item from first scene (excluding isLastItem)
      const firstSceneItemIndex = randomizedEvidence.findIndex(
        (item) => item.sceneId === firstSceneId && !item.isLastItem,
      );

      // Swap it into the first active clue slot if found
      if (firstSceneItemIndex !== -1) {
        initialActiveClueIndices[0] = firstSceneItemIndex;
      }
    }
  }

  return {
    evidence: randomizedEvidence,
    activeClueIndices: initialActiveClueIndices,
    foundEvidence: [],
    unlockedSceneIds: initialUnlockedScenes,
  };
};

/**
 * Checks if finding an item completes a scene and unlocks the next scene(s) in sequential mode.
 * Returns newly unlocked scene IDs (including any consecutive empty scenes after the next one).
 */
export const checkSequentialSceneUnlocking = (
  foundItemSceneId: string,
  currentLocation: Location,
  evidence: ItemWithScene[],
  foundEvidence: ItemWithScene[],
  unlockedSceneIds: string[],
): string[] => {
  const scenes = currentLocation.scenes;

  // Find the scene index of the found item's scene
  const sceneIndex = scenes.findIndex((s) => s.id === foundItemSceneId);
  if (sceneIndex === -1) return [];

  // Check if ALL items in this scene are now found
  const sceneItems = evidence.filter((e) => e.sceneId === foundItemSceneId);
  const foundSceneItems = foundEvidence.filter(
    (e) => e.sceneId === foundItemSceneId,
  );
  if (foundSceneItems.length < sceneItems.length) return [];

  // This scene is complete - unlock the next scene(s)
  const newlyUnlocked: string[] = [];
  for (let i = sceneIndex + 1; i < scenes.length; i++) {
    const nextScene = scenes[i];
    if (unlockedSceneIds.includes(nextScene.id)) continue; // Already unlocked
    newlyUnlocked.push(nextScene.id);
    if (nextScene.items.length > 0) break; // Stop at first scene with items
  }

  return newlyUnlocked;
};

/**
 * Checks if a found item unlocks any scenes in the current location
 */
export const checkSceneUnlocking = (
  foundItemId: string,
  currentLocation: Location,
): string[] => {
  const newlyUnlockedScenes: string[] = [];

  currentLocation.scenes.forEach((scene) => {
    if (scene.unlockRequiredItemId === foundItemId) {
      newlyUnlockedScenes.push(scene.id);
    }
  });

  return newlyUnlockedScenes;
};

/**
 * Calculates which overlays should be queued based on the current game state
 */
export const calculateOverlaysToQueue = (
  _previousFoundCount: number,
  _currentFoundCount: number,
  _requiredCount: number,
  _totalEvidenceCount: number,
  _willLevelEndWithMinimum: boolean,
): QueuedOverlay[] => {
  // No overlays to queue — minimum secured concept removed (no timer mode)
  return [];
};

/**
 * Finds the index of an unlocked scene for navigation purposes
 */
export const findUnlockedSceneIndex = (
  sceneId: string,
  currentLocation: Location | null,
): number | null => {
  if (!currentLocation) return null;

  const unlockedSceneIndex = currentLocation.scenes.findIndex(
    (scene) => scene.id === sceneId,
  );

  return unlockedSceneIndex !== -1 ? unlockedSceneIndex : null;
};

/**
 * Finds the next unfound evidence item from unlocked scenes to add to active clues
 * Note: isLastItem items are only returned when all other items have been found
 */
export const findNextActiveClue = (
  evidence: ItemWithScene[],
  foundEvidence: ItemWithScene[],
  activeClueIndices: number[],
  foundItemIndex: number,
  unlockedSceneIds: string[],
  _currentLocation: Location | null,
): number | null => {
  // Calculate if all non-lastItem evidence has been found
  // This determines whether we can now show the last item
  // Note: foundEvidence already includes the just-found item (from markEvidenceAsFound dispatch)
  const allNonLastItemsFound = areAllNonLastItemsFound(evidence, foundEvidence);

  // Find the next unfound item
  const foundIndices = new Set([
    ...foundEvidence.map((item) =>
      evidence.findIndex((evidenceItem) => evidenceItem.id === item.id),
    ),
    foundItemIndex,
  ]);

  // Find next unfound item from unlocked scenes
  for (let i = 0; i < evidence.length; i++) {
    if (!foundIndices.has(i) && !activeClueIndices.includes(i)) {
      const evidenceItem = evidence[i];

      // Skip lastItem UNLESS all other items are found
      if (evidenceItem.isLastItem && !allNonLastItemsFound) {
        continue;
      }

      // Check if scene is unlocked (use unlockedSceneIds as canonical source of truth)
      const isUnlocked = unlockedSceneIds.includes(evidenceItem.sceneId);

      if (isUnlocked) {
        return i;
      }
    }
  }

  return null;
};
